import { Checkbox, Chip } from '@mui/material';
import React, { CSSProperties, useState } from 'react';
import { reject, append } from 'ramda';
import classNames from 'classnames';
import { Button } from '@progress/kendo-react-buttons';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Error as KendoError } from '@progress/kendo-react-labels';
import { plusIcon, searchIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Lookup } from '../../../Interfaces/StipulationTypes';
import EditorCard from '../EditorCard/EditorCard';
import styles from './_stipulations-editor.module.scss';
import {
  CUSTOM_STIP_NAME_MAX_LENGTH,
  extractApiErrorMessage,
  findExactDuplicate,
  findMatchingCategories,
  normalizeStipName,
  validateCustomStipName,
} from '../../../utils/stipulationUtils';
import { filterByLabel } from '../../../utils/decisionDialogUtils';

export interface StipulationsEditorProps {
  onChange: (stipulationCategories: Lookup[]) => void;
  stipulationCategories: Lookup[];
  selectedItems: Lookup[] | undefined; // Made optional to handle undefined case
  indirectStipulationDynamicTextDictionary: { [key: number]: string };
  style?: CSSProperties;
  /** Renders the "+ Add custom stip" control (permission-gated by the caller). */
  canAddCustomStip?: boolean;
  /** Creates the custom stip category on the server; the caller refreshes the list and checks the new entry. */
  onAddCustomStip?: (name: string) => Promise<void>;
  /** Single scrolling column with no filter box, for when this editor shares a row with the reasons editor. */
  compact?: boolean;
  /** Inline validation shown in the card header. */
  errorMessage?: string;
}

const MAX_MATCHES_SHOWN = 5;
const ADD_FAILED_MESSAGE = 'Unable to add the custom stipulation. Please try again.';

export default function StipulationsEditor(props: StipulationsEditorProps) {
  // Ensure we have an array even if selectedItems is undefined
  const selectedItems = props.selectedItems || [];

  const [filterText, setFilterText] = useState('');
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customStipName, setCustomStipName] = useState('');
  const [customStipError, setCustomStipError] = useState<string | undefined>(undefined);
  const [isSavingCustom, setIsSavingCustom] = useState(false);

  const matchingCategories = findMatchingCategories(props.stipulationCategories, customStipName);
  const exactDuplicate = findExactDuplicate(props.stipulationCategories, customStipName);
  const duplicateMessage = exactDuplicate ? `"${exactDuplicate.name}" already exists. Check it in the list instead.` : undefined;
  const canSubmitCustom = !isSavingCustom && !exactDuplicate && validateCustomStipName(customStipName) === undefined;

  // The dealer receives the dynamic text when one exists, so that is what the list shows.
  const labelFor = (stipulationCategory: Lookup) =>
    props.indirectStipulationDynamicTextDictionary[stipulationCategory.id as number] ?? stipulationCategory.name;

  const visibleCategories = filterByLabel(props.stipulationCategories, filterText, labelFor);

  const isStipulationChecked = (stipulationId: string | number) => selectedItems.some(s => s.id === stipulationId);

  const toggleStipulation = (stipulationCategory: Lookup) => {
    const newSelectedStipulations = isStipulationChecked(stipulationCategory.id)
      ? reject((s: Lookup) => s.id === stipulationCategory.id, selectedItems)
      : append(stipulationCategory, selectedItems);
    props.onChange(newSelectedStipulations);
  };

  // Check if all stipulations are selected
  const areAllStipulationsSelected = () =>
    props.stipulationCategories.length > 0 &&
    props.stipulationCategories.every(stip => selectedItems.some(selectedStip => selectedStip.id === stip.id));

  // Select-all works on the whole list, not just the filtered rows, so a stale filter cannot hide a selection.
  const handleSelectAll = () => {
    props.onChange(areAllStipulationsSelected() ? [] : [...props.stipulationCategories]);
  };

  const resetCustomStipEntry = () => {
    setIsAddingCustom(false);
    setCustomStipName('');
    setCustomStipError(undefined);
  };

  const submitCustomStip = async () => {
    const validationError = validateCustomStipName(customStipName) ?? duplicateMessage;
    if (validationError) {
      setCustomStipError(validationError);
      return;
    }
    if (!props.onAddCustomStip) {
      return;
    }

    setIsSavingCustom(true);
    setCustomStipError(undefined);
    try {
      await props.onAddCustomStip(normalizeStipName(customStipName));
      resetCustomStipEntry();
    } catch (error) {
      setCustomStipError(extractApiErrorMessage(error, ADD_FAILED_MESSAGE));
    } finally {
      setIsSavingCustom(false);
    }
  };

  const headerActions = (
    <>
      {!props.compact && (
        <TextBox
          className={styles.filterBox}
          size="small"
          value={filterText}
          onChange={(e: TextBoxChangeEvent) => setFilterText((e.target.value as string) ?? '')}
          placeholder="Filter stipulations"
          prefix={() => <SvgIcon icon={searchIcon} className={styles.filterIcon} />}
        />
      )}
      <Button fillMode="flat" themeColor="primary" size="small" onClick={handleSelectAll} disabled={props.stipulationCategories.length === 0}>
        {areAllStipulationsSelected() ? 'Clear all' : 'Select all'}
      </Button>
    </>
  );

  return (
    <EditorCard
      title="Stipulations"
      count={selectedItems.length}
      errorMessage={props.errorMessage}
      titleStyle={props.style}
      actions={headerActions}
      className={styles.stipulationsEditorContainer}
    >
      <div className={classNames(styles.selectedRow, { [styles.selectedRowCompact]: props.compact })}>
        <span className={styles.selectedLabel}>Sending</span>
        {selectedItems.length === 0 && <span className={styles.selectedEmpty}>Nothing selected yet</span>}
        {selectedItems.map(stipulationCategory => (
          <Chip
            key={stipulationCategory.id}
            size="small"
            className={styles.selectedChip}
            label={
              <>
                {stipulationCategory.isCustom && <span className={styles.chipCustomPrefix}>Custom &middot; </span>}
                {labelFor(stipulationCategory)}
              </>
            }
            onDelete={() => toggleStipulation(stipulationCategory)}
          />
        ))}
      </div>
      <div className={props.compact ? styles.stipulationGridCompact : styles.stipulationGrid}>
        {visibleCategories.map((stipulationCategory: Lookup) => {
          const checked = isStipulationChecked(stipulationCategory.id);
          // A native label wrapping the checkbox keeps the input focusable and associates the text with it.
          return (
            <label
              key={stipulationCategory.id}
              className={classNames(styles.stipulationRow, { [styles.stipulationRowChecked]: checked })}
            >
              <Checkbox
                size="small"
                edge="start"
                checked={checked}
                onChange={() => toggleStipulation(stipulationCategory)}
                disableRipple
                className={styles.rowCheckbox}
              />
              <span className={styles.rowLabel}>{labelFor(stipulationCategory)}</span>
              {stipulationCategory.isCustom && (
                <Chip label="Custom" size="small" variant="outlined" color="primary" className={styles.customChip} />
              )}
            </label>
          );
        })}
        {visibleCategories.length === 0 && props.stipulationCategories.length > 0 && (
          <span className={styles.noMatches}>No stipulations match "{filterText.trim()}"</span>
        )}
      </div>
      {props.canAddCustomStip && props.onAddCustomStip && (
        <div className={styles.addCustomStip}>
          {!isAddingCustom ? (
            <Button fillMode="flat" themeColor="primary" size="small" svgIcon={plusIcon} onClick={() => setIsAddingCustom(true)}>
              Add custom stip
            </Button>
          ) : (
            <div className={styles.addCustomStipEntry}>
              <div className={styles.addCustomStipRow}>
                <TextBox
                  value={customStipName}
                  onChange={(e: TextBoxChangeEvent) => {
                    setCustomStipName((e.target.value as string) ?? '');
                    setCustomStipError(undefined);
                  }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && canSubmitCustom) {
                      e.preventDefault();
                      void submitCustomStip();
                    } else if (e.key === 'Escape') {
                      resetCustomStipEntry();
                    }
                  }}
                  placeholder="New stipulation name"
                  maxLength={CUSTOM_STIP_NAME_MAX_LENGTH}
                  autoFocus
                  disabled={isSavingCustom}
                  style={{ flex: 1 }}
                />
                <Button themeColor="primary" onClick={() => void submitCustomStip()} disabled={!canSubmitCustom}>
                  {isSavingCustom ? 'Adding…' : 'Add'}
                </Button>
                <Button fillMode="flat" onClick={resetCustomStipEntry} disabled={isSavingCustom}>
                  Cancel
                </Button>
              </div>
              {(customStipError || duplicateMessage) && <KendoError>{customStipError ?? duplicateMessage}</KendoError>}
              {!exactDuplicate && matchingCategories.length > 0 && (
                <div className={styles.customStipMatches}>
                  <span>Existing entries that match:</span>
                  <ul>
                    {matchingCategories.slice(0, MAX_MATCHES_SHOWN).map(match => (
                      <li key={match.id}>{match.name}</li>
                    ))}
                    {matchingCategories.length > MAX_MATCHES_SHOWN && <li>…and {matchingCategories.length - MAX_MATCHES_SHOWN} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </EditorCard>
  );
}
